/* eslint-disable lingui/no-unlocalized-strings */
import { SocialLogin } from "@capgo/capacitor-social-login";
import { Capacitor } from "@capacitor/core";
import { fetcher } from "./utility";
import { store } from "../";
import { eventGA, setIdGA } from "./TrackerGA";
import { setUser } from "../actions/user";
import { emitMessage } from "../actions/socket";
import { setHome, homeSetSlots, homeSetNotifications } from "../actions/home";
import { setConversationPreview } from "../actions/conversation";
import * as Sentry from "@sentry/react";
import { saveAccessToken, clearAuthData } from "./authPersistence";
import { facebookRedirect } from "./facebookUtility";

let isInitialized = false;

type Provider = "facebook" | "google" | "apple";

// Initialize the social login plugin
export const initializeSocialLogin = async () => {
  if (isInitialized) return;

  try {
    await SocialLogin.initialize({
      facebook: {
        appId: "309826149501197",
        clientToken: "5bdea72a42c15ecaeaa3bd3107eada66",
      },
      google: {
        webClientId:
          "830046400557-f7au6r4qvkn6jcku7r7l2vekq2irhqsu.apps.googleusercontent.com",
        iOSClientId:
          "830046400557-jkele44u7r6stl7rcocvbulc0o5gnsa1.apps.googleusercontent.com",
        iOSServerClientId:
          "com.googleusercontent.apps.830046400557-jkele44u7r6stl7rcocvbulc0o5gnsa1",
      },
    });
    isInitialized = true;
  } catch (error) {
    console.error("Failed to initialize social login:", error);
    Sentry.captureException(error);
  }
};

// Common login processing function
const processLoginResponse = (res: any, provider: Provider) => {
  if (!res.success) {
    return { success: false, error: res.error };
  }

  // Track analytics for new users
  if (res.newUser) {
    eventGA(
      "ACQUISIZIONI",
      `Registrazione_${provider}`,
      `User_${res.user._id}`,
      res.user.city
    );
    res.user.new = true;
    res.user.pwaInstallSeen = true;
  }

  res.user.logged = true;
  setIdGA(res.user._id);

  // Save auth data for persistent login with JWT token
  saveAccessToken(res.accessToken);

  // Process tenants data
  res.user.tenants.forEach((tenant: any, index: number) => {
    if (tenant.user_id) {
      let user_tenant = res.user_tenants.find(
        (x: any) => x._id == tenant.user_id
      );
      if (user_tenant) {
        let pickedUser: any = {};
        pickedUser.value = user_tenant._id;
        pickedUser.image = user_tenant.profile_image;
        pickedUser.image_webp = user_tenant.profile_image_webp;
        pickedUser.location = user_tenant.location;
        pickedUser.age = user_tenant.age;
        pickedUser.type = user_tenant.type;
        pickedUser.label = user_tenant.name + " " + user_tenant.surname;
        res.user.tenants[index].pickedUser = pickedUser;
        res.user.tenants[index].image = user_tenant.profile_image;
        res.user.tenants[index].image_webp = user_tenant.profile_image_webp;
      }
    }
  });

  // Update Redux store
  store.dispatch(setUser(res.user));

  if (res.notifications != null) {
    store.dispatch(homeSetNotifications(res.notifications));
  }

  if (res.home != null) {
    res.home.tenants.forEach((tenant: any, i_t: number) => {
      if (tenant.user_id) {
        let home_tenant = res.home_tenants.find(
          (x: any) => x._id == tenant.user_id
        );
        if (home_tenant) {
          let pickedUser: any = {};
          pickedUser.value = home_tenant._id;
          pickedUser.image = home_tenant.profile_image;
          pickedUser.image_webp = home_tenant.profile_image_webp;
          pickedUser.location = home_tenant.location;
          pickedUser.age = home_tenant.age;
          pickedUser.type = home_tenant.type;
          pickedUser.label = home_tenant.name + " " + home_tenant.surname;
          res.home.tenants[i_t].pickedUser = pickedUser;
          res.home.tenants[i_t].image = home_tenant.profile_image;
          res.home.tenants[i_t].image_webp = home_tenant.profile_image_webp;
        }
      }
    });

    if (res.ads != null) {
      res.home.advertises = res.ads;
    }
    store.dispatch(setHome(res.home));

    if (res.slotArray != null) {
      store.dispatch(homeSetSlots(res.slotArray));
    }
  }

  if (res.conversations != null) {
    store.dispatch(setConversationPreview(res.conversations));
  }

  // Emit socket events
  if (res.home != null) {
    store.dispatch(emitMessage("login_home", res.home._id));
    store.dispatch(emitMessage("login_user", res.user._id));
  } else {
    store.dispatch(emitMessage("login_user", res.user._id));
  }

  return { success: true, user: res.user };
};

// Facebook login.
// On native (iOS/Android) the Capgo plugin uses the native Facebook SDK,
// which surfaces the requested scopes in the consent dialog as expected.
// On web, the plugin falls back to `FB.login()` which uses the implicit
// (token) flow; under Meta's current DUC restrictions for `user_birthday`
// and `user_gender`, those scopes are silently stripped from the dialog
// in the token flow. Switching to a manual redirect-based authorization
// code flow shows them as expected.
export const facebookLogin = async () => {
  try {
    if (!Capacitor.isNativePlatform()) {
      // Web → manual code-flow redirect. Page navigates away; on return,
      // App.jsx detects ?code= in the URL and posts it to the backend.
      facebookRedirect("/");
      // Caller should not act on this result — the page is redirecting.
      return { success: true, pending: true };
    }

    await initializeSocialLogin();

    const result = await SocialLogin.login({
      provider: "facebook",
      options: {
        permissions: [
          "email",
          "public_profile",
          "user_birthday",
          "user_gender",
        ],
      },
    });

    if (result.result.accessToken || result.result.idToken) {
      const res = await fetcher({
        path: "/api/login_facebook",
        credentials: true,
        redirect: "follow",
        method: "POST",
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
        body: {
          access_token: result.result.accessToken?.token,
          id_token: result.result.idToken,
        },
      });

      return processLoginResponse(res, "facebook");
    }

    return { success: false, error: "No access token received" };
  } catch (error: any) {
    console.error("Facebook login error:", error);
    Sentry.captureException(error);
    return { success: false, error: error.message };
  }
};

// Google login using the unified plugin
export const googleLogin = async () => {
  try {
    await initializeSocialLogin();

    const result = await SocialLogin.login({
      provider: "google",
      options: {
        scopes: ["email", "profile"],
      },
    });

    if (
      ("accessToken" in result.result && result.result.accessToken) ||
      ("idToken" in result.result && result.result.idToken)
    ) {
      const res = await fetcher({
        path: "/api/login_google",
        credentials: true,
        redirect: "follow",
        method: "POST",
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
        body: {
          accessToken: result.result.accessToken?.token,
          idToken: result.result.idToken,
          credential: result.result.idToken, // Google also expects 'credential' field
        },
      });

      return processLoginResponse(res, "google");
    }

    return { success: false, error: "No credential received" };
  } catch (error: any) {
    console.error("Google login error:", error);
    Sentry.captureException(error);
    return { success: false, error: error.message };
  }
};

// Check if user is logged in
export const isLoggedIn = async (provider: Provider) => {
  try {
    await initializeSocialLogin();

    const result = await SocialLogin.isLoggedIn({
      provider: provider,
    });

    return result.isLoggedIn;
  } catch (error) {
    console.error(`${provider} isLoggedIn error:`, error);
    return false;
  }
};

// Logout from social provider
export const socialLogout = async (provider: Provider) => {
  try {
    await initializeSocialLogin();

    return await SocialLogin.logout({
      provider: provider,
    });
  } catch (error: any) {
    console.error(`${provider} logout error:`, error);
    Sentry.captureException(error);
    return { success: false, error: error.message };
  }
};

// Complete logout function that handles both API logout and social logout
export const performLogout = async (
  user: any,
  setLoggedCallback: (logged: boolean) => void
) => {
  try {
    // Clear stored auth data
    await clearAuthData();

    await fetcher({
      path: "/api/logOut",
      credentials: true,
      redirect: "follow",
    });

    setLoggedCallback(false);

    // Handle social logout for Capacitor
    if (user.social === "facebook") {
      // Skip the SocialLogin plugin's FB.logout() on web
      if (Capacitor.isNativePlatform()) {
        await socialLogout("facebook");
      }
      window.location.reload();
    } else if (user.social === "google") {
      // Capacitor Google logout
      await socialLogout("google");
      window.location.reload();
    } else if (user.social === "apple") {
      // Capacitor Apple logout
      await socialLogout("apple");
      window.location.reload();
    } else {
      window.location.reload();
    }
  } catch (e) {
    Sentry.captureException(e);
    // Fallback: reload anyway to ensure user is logged out
    window.location.reload();
  }
};
